import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    IconButton,
    Grid,
    Divider,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    Save as SaveIcon,
    Map as MapIcon,
    LocationOn as LocationOnIcon,
    Route as RouteIcon,
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import MapPickerDialog from '../../../components/MapPickerDialog';
import JsdhNotifikaceService from '../../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import { useToast } from '../../../components/toast/useToast';
import type { JednotkaDto } from '../../../api/JsdhNotifikaceService/generated/models/JednotkaDto';
import type { LokaceSearchResult } from '../../../api/JsdhNotifikaceService/generated/models/LokaceSearchResult';
import { jsouPresetyPlatne, pripravJednotkuKUlozeni } from './dojezdPresety';
import type {
    useUpdateJednotkaMutation,
    useUploadLogoMutation,
    useDeleteLogoMutation,
} from '../../../api/JsdhNotifikaceService/service/AdminMutations';

// Predefined departure time options
const CAS_VYJEZDU_OPTIONS = [2, 5, 10];

// Max logo file size (5 MB)
const MAX_LOGO_SIZE_MB = 5;
const MAX_LOGO_SIZE_BYTES = MAX_LOGO_SIZE_MB * 1024 * 1024;

interface JednotkaTabProps {
    jednotka: JednotkaDto | undefined;
    jednotkaForm: JednotkaDto;
    setJednotkaForm: React.Dispatch<React.SetStateAction<JednotkaDto>>;
    isAdmin: boolean;
    updateJednotkaMutation: ReturnType<typeof useUpdateJednotkaMutation>;
    uploadLogoMutation: ReturnType<typeof useUploadLogoMutation>;
    deleteLogoMutation: ReturnType<typeof useDeleteLogoMutation>;
    refetchJednotka: () => void;
}


const JednotkaTab: React.FC<JednotkaTabProps> = ({
    jednotka,
    jednotkaForm,
    setJednotkaForm,
    isAdmin,
    updateJednotkaMutation,
    uploadLogoMutation,
    deleteLogoMutation,
    refetchJednotka,
}) => {
    const { showToast } = useToast();

    // Logo state
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Cas vyjezdu state
    const [casVyjezduCustom, setCasVyjezduCustom] = useState(false);
    // Tri textova pole nabizenych hodnot dojezdu. Drzi se jako retezce, aby sla
    // pole vyprazdnit pri psani, aniz by se do DTO dostalo NaN.
    const [poleDojezd, setPoleDojezd] = useState<string[]>(['2', '5', '8']);

    // Zbrojnice autocomplete state
    const [zbrojniceAdresaInput, setZbrojniceAdresaInput] = useState('');
    const [zbrojniceLokaceOptions, setZbrojniceLokaceOptions] = useState<LokaceSearchResult[]>([]);
    const [isLoadingZbrojniceLokace, setIsLoadingZbrojniceLokace] = useState(false);
    const zbrojniceLokaceSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [zbrojniceEditing, setZbrojniceEditing] = useState(false);

    // Map dialog state
    const [mapDialogOpen, setMapDialogOpen] = useState(false);

    // Initialize logo preview and cas vyjezdu from jednotka
    useEffect(() => {
        if (jednotka) {
            if (jednotka.maLogo) {
                setLogoPreview(JsdhNotifikaceService.getJednotkaLogoUrl() + `?t=${Date.now()}`);
            }
            if (jednotka.dojezdPresety?.length === 3) {
                setPoleDojezd(jednotka.dojezdPresety.map(String));
            }
            if (jednotka.casVyjezdMinuty && !CAS_VYJEZDU_OPTIONS.includes(jednotka.casVyjezdMinuty)) {
                setCasVyjezduCustom(true);
            }
        }
    }, [jednotka]);

    // Handlers
    const handleJednotkaChange = (field: keyof JednotkaDto) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.type === 'number' ? Number(e.target.value) || undefined : e.target.value;
        setJednotkaForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSaveJednotka = () => {
        updateJednotkaMutation.mutate(pripravJednotkuKUlozeni(jednotkaForm, poleDojezd), {
            onSuccess: () => {
                refetchJednotka();
            },
        });
    };

    const handleCasVyjezduChange = (value: string) => {
        if (value === 'custom') {
            setCasVyjezduCustom(true);
        } else {
            setCasVyjezduCustom(false);
            setJednotkaForm((prev) => ({
                ...prev,
                casVyjezdMinuty: Number(value),
            }));
        }
    };

    // Logo handlers
    const handleLogoClick = () => {
        fileInputRef.current?.click();
    };

    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > MAX_LOGO_SIZE_BYTES) {
                showToast(`Logo je příliš velké. Maximální velikost je ${MAX_LOGO_SIZE_MB} MB.`, 'error');
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }

            uploadLogoMutation.mutate(file, {
                onSuccess: () => {
                    setLogoPreview(JsdhNotifikaceService.getJednotkaLogoUrl() + `?t=${Date.now()}`);
                    refetchJednotka();
                },
            });
        }
    };

    const handleDeleteLogo = () => {
        deleteLogoMutation.mutate(undefined, {
            onSuccess: () => {
                setLogoPreview(null);
                refetchJednotka();
            },
        });
    };

    // Zbrojnice autocomplete handlers
    const handleZbrojniceAdresaInputChange = (value: string) => {
        setZbrojniceAdresaInput(value);

        if (zbrojniceLokaceSearchTimeoutRef.current) {
            clearTimeout(zbrojniceLokaceSearchTimeoutRef.current);
        }

        if (value.length >= 2) {
            zbrojniceLokaceSearchTimeoutRef.current = setTimeout(async () => {
                setIsLoadingZbrojniceLokace(true);
                try {
                    const results = await JsdhNotifikaceService.searchLokace(value);
                    setZbrojniceLokaceOptions(results);
                } catch {
                    setZbrojniceLokaceOptions([]);
                }
                setIsLoadingZbrojniceLokace(false);
            }, 300);
        } else {
            setZbrojniceLokaceOptions([]);
        }
    };

    const handleZbrojniceLokaceSelect = (value: string | LokaceSearchResult | null) => {
        if (!value) return;

        if (typeof value === 'string') {
            setZbrojniceAdresaInput(value);
        } else {
            setZbrojniceAdresaInput(value.displayName);
            const updatedForm = {
                ...jednotkaForm,
                zbrojniceKraj: value.kraj || jednotkaForm.zbrojniceKraj,
                zbrojniceOkres: value.okres || jednotkaForm.zbrojniceOkres,
                zbrojniceObec: value.obec || value.nazev,
                zbrojniceCastObce: value.castObce || jednotkaForm.zbrojniceCastObce,
                zbrojniceUlice: value.ulice || jednotkaForm.zbrojniceUlice,
                zbrojniceCisloPopisne: value.cisloPopisne || jednotkaForm.zbrojniceCisloPopisne,
                zbrojnicePsc: value.psc || jednotkaForm.zbrojnicePsc,
                zbrojniceLat: value.lat,
                zbrojniceLng: value.lng,
            };
            setJednotkaForm(updatedForm);
            updateJednotkaMutation.mutate(updatedForm, {
                onSuccess: () => {
                    refetchJednotka();
                },
            });
        }
    };

    const handleMapSelect = (lat: number, lng: number) => {
        setJednotkaForm((prev) => ({
            ...prev,
            zbrojniceLat: lat,
            zbrojniceLng: lng,
        }));
    };

    // Computed values
    const gpsString = jednotkaForm.zbrojniceLat && jednotkaForm.zbrojniceLng
        ? `${jednotkaForm.zbrojniceLat},${jednotkaForm.zbrojniceLng}`
        : '';
    const zbrojniceFilled = !!(jednotkaForm.zbrojniceObec && gpsString);
    const zbrojniceReadOnly = zbrojniceFilled && !zbrojniceEditing;

    const geocodedGps: [number, number] | null = jednotkaForm.zbrojniceLat && jednotkaForm.zbrojniceLng
        ? [jednotkaForm.zbrojniceLat, jednotkaForm.zbrojniceLng]
        : null;

    return (
        <>
            <Paper sx={{ p: 3 }}>
                {/* === Sekce 1: Údaje o jednotce === */}
                <Typography variant="h6" gutterBottom>
                    Údaje o jednotce
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>
                    {/* Logo */}
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Box
                                onClick={isAdmin ? handleLogoClick : undefined}
                                sx={{
                                    width: { xs: 80, sm: 120 },
                                    height: { xs: 80, sm: 120 },
                                    border: '2px dashed',
                                    borderColor: 'grey.400',
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: isAdmin ? 'pointer' : 'default',
                                    overflow: 'hidden',
                                    bgcolor: 'grey.50',
                                    '&:hover': isAdmin ? {
                                        borderColor: 'primary.main',
                                        bgcolor: 'grey.100',
                                    } : {},
                                }}
                            >
                                {logoPreview ? (
                                    <img
                                        src={logoPreview}
                                        alt="Logo jednotky"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                        }}
                                    />
                                ) : (
                                    <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                                )}
                            </Box>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleLogoUpload}
                                style={{ display: 'none' }}
                            />
                            {isAdmin && (
                                <Typography variant="caption" color="text.secondary">
                                    Klikněte pro nahrání loga (max {MAX_LOGO_SIZE_MB} MB)
                                </Typography>
                            )}
                            {(uploadLogoMutation.isPending || deleteLogoMutation.isPending) && (
                                <CircularProgress size={20} />
                            )}
                            {isAdmin && logoPreview && (
                                <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleDeleteLogo}
                                    disabled={deleteLogoMutation.isPending}
                                    sx={{ mt: 1 }}
                                >
                                    Smazat logo
                                </Button>
                            )}
                        </Box>
                    </Grid>

                    {/* Basic Info */}
                    <Grid size={{ xs: 12, md: 9 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Název jednotky"
                                    fullWidth
                                    required
                                    disabled={!isAdmin}
                                    value={jednotkaForm.nazev}
                                    onChange={handleJednotkaChange('nazev')}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <TextField
                                    label="Evidenční číslo"
                                    fullWidth
                                    disabled={!isAdmin || !!jednotka?.evidencniCislo}
                                    value={jednotkaForm.evidencniCislo || ''}
                                    onChange={handleJednotkaChange('evidencniCislo')}
                                    placeholder="např. 550311-01"
                                    helperText="Číslo z registru PO"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <FormControl fullWidth disabled={!isAdmin}>
                                    <InputLabel>Čas výjezdu</InputLabel>
                                    <Select
                                        value={
                                            casVyjezduCustom
                                                ? 'custom'
                                                : jednotkaForm.casVyjezdMinuty?.toString() || ''
                                        }
                                        label="Čas výjezdu"
                                        onChange={(e) => handleCasVyjezduChange(e.target.value)}
                                    >
                                        {CAS_VYJEZDU_OPTIONS.map((option) => (
                                            <MenuItem key={option} value={option.toString()}>
                                                {option} minut
                                            </MenuItem>
                                        ))}
                                        <MenuItem value="custom">Vlastní</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {casVyjezduCustom && (
                                <Grid size={{ xs: 12, sm: 3 }}>
                                    <TextField
                                        label="Vlastní čas"
                                        fullWidth
                                        type="number"
                                        disabled={!isAdmin}
                                        value={jednotkaForm.casVyjezdMinuty || ''}
                                        onChange={handleJednotkaChange('casVyjezdMinuty')}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">min</InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            )}
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Dojezd do zbrojnice — hodnoty, které se hasiči nabídnou po stisku JEDU.
                                    Zadávejte vzestupně, 1 až 60 minut.
                                </Typography>
                            </Grid>
                            {poleDojezd.map((hodnota, index) => (
                                <Grid size={{ xs: 4, sm: 2 }} key={`dojezd-${index}`}>
                                    <TextField
                                        label={`Dojezd ${index + 1}`}
                                        fullWidth
                                        type="number"
                                        disabled={!isAdmin}
                                        value={hodnota}
                                        onChange={(e) =>
                                            setPoleDojezd((prev) =>
                                                prev.map((p, i) => (i === index ? e.target.value : p)),
                                            )
                                        }
                                        inputProps={{ min: 1, max: 60 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">min</InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            ))}
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={jednotkaForm.dojezdOdpocetZapnut !== false}
                                            disabled={!isAdmin}
                                            onChange={(e) =>
                                                setJednotkaForm((prev) => ({
                                                    ...prev,
                                                    dojezdOdpocetZapnut: e.target.checked,
                                                }))
                                            }
                                        />
                                    }
                                    label="Zobrazovat odpočet dojezdu na poplachové obrazovce"
                                />
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Platí pro všechny poplachové obrazovky včetně Raspberry Pi a výjezdového
                                    tabletu. Když je vypnuto, hasiči si čas dál mohou zvolit v mobilní
                                    aplikaci, jen se u odpovědí nezobrazuje odpočet.
                                </Typography>
                            </Grid>
                            {jednotkaForm.casVyjezdMinuty != null &&
                                poleDojezd.some(
                                    (v) => Number.parseInt(v, 10) > (jednotkaForm.casVyjezdMinuty ?? 0),
                                ) && (
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="caption" color="warning.main">
                                        Hodnoty vyšší než čas pro výjezd ({jednotkaForm.casVyjezdMinuty} min)
                                        se na poplachové obrazovce nezobrazí — odpočet u všech mizí,
                                        jakmile čas pro výjezd uplyne.
                                    </Typography>
                                </Grid>
                            )}
                            {!jsouPresetyPlatne(poleDojezd.map((v) => Number.parseInt(v, 10))) && (
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="caption" color="error">
                                        Zadejte tři vzestupné hodnoty 1 až 60 minut. Dokud to neplatí,
                                        uloží se ostatní změny a hodnoty dojezdu zůstanou beze změny.
                                    </Typography>
                                </Grid>
                            )}
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Email"
                                    fullWidth
                                    type="email"
                                    disabled={!isAdmin}
                                    value={jednotkaForm.email || ''}
                                    onChange={handleJednotkaChange('email')}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    label="Telefon"
                                    fullWidth
                                    disabled={!isAdmin}
                                    value={jednotkaForm.telefon || ''}
                                    onChange={handleJednotkaChange('telefon')}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {/* === Sekce 2: Adresa zbrojnice === */}
                <Divider sx={{ my: 3 }} />

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <LocationOnIcon color="action" />
                        <Typography variant="h6">
                            Adresa zbrojnice
                        </Typography>
                    </Box>
                    {isAdmin && zbrojniceFilled && (
                        <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => setZbrojniceEditing(!zbrojniceEditing)}
                        >
                            {zbrojniceEditing ? 'Zrušit úpravy' : 'Upravit'}
                        </Button>
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} mb={2}>
                    <RouteIcon sx={{ fontSize: 14 }} />
                    Slouží pro výpočet trasy k místu zásahu
                </Typography>

                {zbrojniceReadOnly ? (
                    <Box>
                        <Typography variant="body1" fontWeight="medium">
                            {[
                                jednotkaForm.zbrojniceUlice,
                                jednotkaForm.zbrojniceCisloPopisne,
                            ].filter(Boolean).join(' ')}
                            {(jednotkaForm.zbrojniceUlice || jednotkaForm.zbrojniceCisloPopisne) && ', '}
                            {jednotkaForm.zbrojniceObec}
                            {jednotkaForm.zbrojniceCastObce ? ` - ${jednotkaForm.zbrojniceCastObce}` : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                            {[
                                jednotkaForm.zbrojnicePsc,
                                jednotkaForm.zbrojniceOkres,
                                jednotkaForm.zbrojniceKraj,
                            ].filter(Boolean).join(', ')}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                GPS: {gpsString}
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<MapIcon />}
                                onClick={() => setMapDialogOpen(true)}
                            >
                                Zobrazit na mapě
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {/* Autocomplete */}
                        <Grid size={12}>
                            <Autocomplete
                                freeSolo
                                options={zbrojniceLokaceOptions}
                                loading={isLoadingZbrojniceLokace}
                                getOptionLabel={(option) => typeof option === 'string' ? option : option.nazev}
                                filterOptions={(x) => x}
                                inputValue={zbrojniceAdresaInput}
                                onInputChange={(_, value) => handleZbrojniceAdresaInputChange(value)}
                                onChange={(_, value) => handleZbrojniceLokaceSelect(value)}
                                disabled={!isAdmin}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Vyhledat adresu zbrojnice"
                                        fullWidth
                                        placeholder="Zadejte obec, ulici nebo adresu..."
                                        helperText="Vyberte z nabídky pro automatické vyplnění polí"
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const { key, ...rest } = props;
                                    return (
                                        <li key={option.id} {...rest}>
                                            <Box>
                                                <Typography>{option.nazev}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.displayName}
                                                </Typography>
                                            </Box>
                                        </li>
                                    );
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                label="Kraj"
                                fullWidth
                                disabled={!isAdmin}
                                value={jednotkaForm.zbrojniceKraj || ''}
                                onChange={handleJednotkaChange('zbrojniceKraj')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                label="Okres"
                                fullWidth
                                disabled={!isAdmin}
                                value={jednotkaForm.zbrojniceOkres || ''}
                                onChange={handleJednotkaChange('zbrojniceOkres')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                label="Obec"
                                fullWidth
                                disabled={!isAdmin}
                                value={jednotkaForm.zbrojniceObec || ''}
                                onChange={handleJednotkaChange('zbrojniceObec')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                label="Část obce"
                                fullWidth
                                disabled={!isAdmin}
                                value={jednotkaForm.zbrojniceCastObce || ''}
                                onChange={handleJednotkaChange('zbrojniceCastObce')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                label="Ulice"
                                fullWidth
                                disabled={!isAdmin}
                                value={jednotkaForm.zbrojniceUlice || ''}
                                onChange={handleJednotkaChange('zbrojniceUlice')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                            <TextField
                                label="Č.p."
                                fullWidth
                                disabled={!isAdmin}
                                value={jednotkaForm.zbrojniceCisloPopisne || ''}
                                onChange={handleJednotkaChange('zbrojniceCisloPopisne')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                            <TextField
                                label="PSČ"
                                fullWidth
                                disabled={!isAdmin}
                                value={jednotkaForm.zbrojnicePsc || ''}
                                onChange={handleJednotkaChange('zbrojnicePsc')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                label="GPS souřadnice"
                                fullWidth
                                disabled={!isAdmin}
                                value={gpsString}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: isAdmin ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setMapDialogOpen(true)}
                                                edge="end"
                                            >
                                                <MapIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : undefined,
                                }}
                            />
                        </Grid>
                    </Grid>
                )}

                {/* === Sekce 3: Adresa zřizovatele === */}
                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>
                    Adresa zřizovatele
                </Typography>

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            label="IČO"
                            fullWidth
                            disabled={!isAdmin}
                            value={jednotkaForm.ico || ''}
                            onChange={handleJednotkaChange('ico')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            label="Kraj"
                            fullWidth
                            disabled={!isAdmin}
                            value={jednotkaForm.adresaKraj || ''}
                            onChange={handleJednotkaChange('adresaKraj')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            label="Okres"
                            fullWidth
                            disabled={!isAdmin}
                            value={jednotkaForm.adresaOkres || ''}
                            onChange={handleJednotkaChange('adresaOkres')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            label="Obec"
                            fullWidth
                            disabled={!isAdmin}
                            value={jednotkaForm.adresaObec || ''}
                            onChange={handleJednotkaChange('adresaObec')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            label="Část obce"
                            fullWidth
                            disabled={!isAdmin}
                            value={jednotkaForm.adresaCastObce || ''}
                            onChange={handleJednotkaChange('adresaCastObce')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            label="Ulice"
                            fullWidth
                            disabled={!isAdmin}
                            value={jednotkaForm.adresaUlice || ''}
                            onChange={handleJednotkaChange('adresaUlice')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                        <TextField
                            label="Č.p."
                            fullWidth
                            disabled={!isAdmin}
                            value={jednotkaForm.adresaCisloPopisne || ''}
                            onChange={handleJednotkaChange('adresaCisloPopisne')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                        <TextField
                            label="PSČ"
                            fullWidth
                            disabled={!isAdmin}
                            value={jednotkaForm.adresaPsc || ''}
                            onChange={handleJednotkaChange('adresaPsc')}
                        />
                    </Grid>
                </Grid>

                {/* Save button */}
                {isAdmin && (
                    <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveJednotka}
                            disabled={updateJednotkaMutation.isPending || !jednotkaForm.nazev}
                        >
                            {updateJednotkaMutation.isPending ? 'Ukládám...' : 'Uložit změny'}
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Map Picker Dialog */}
            <MapPickerDialog
                open={mapDialogOpen}
                onClose={() => setMapDialogOpen(false)}
                onSelect={handleMapSelect}
                initialGps={gpsString}
                geocodedObecGps={geocodedGps}
            />
        </>
    );
};

export default JednotkaTab;
